import AppContext from 'App/AppContext';
import { IPSeparator, IPSpinner, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import type * as pbi from 'powerbi-client'; //Static models import: forces powerbi-client into initial load
import { lazy, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { powerBIClientRequest } from 'services/Auth/authConfig';
import { isValidGuid } from 'utils/guid';
import './PowerBIReport.css';
import { powerBI_dark } from './PowerBIReportThemes';
import { IPSpinnerSize } from "baseComponents/Spinner/IPSpinner";

interface IPowerBIReportPros {
  url: string;
}

// 1) Lazy-load the React component
const PowerBIEmbed = lazy(() => import('powerbi-client-react').then((m) => ({ default: m.PowerBIEmbed })));

const PowerBIReport = (props: IPowerBIReportPros) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['translation', 'dashboard', 'identityproviders']);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [config, setConfig] = useState<pbi.IReportEmbedConfiguration | undefined>(undefined);
  //the embed token is a Microsoft token: a user of another identity provider has none
  const hasMicrosoftIdentity = appContext.hasMicrosoftIdentity();

  useEffect(() => {
    if (hasMicrosoftIdentity) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getIdFromUrl = (url: string): string | undefined => {
    if (url.includes('?')) {
      const idx = url.indexOf('?');
      const query = url.substring(idx);
      const queryParams = new URLSearchParams(query);
      if (queryParams.has('reportId')) {
        //https://app.powerbi.com/reportEmbed?reportId={id}&autoAuth=true&ctid=f3d70179-6e00-41c9-9c06-3b1771606b98
        return queryParams.get('reportId') || undefined;
      }
    }

    if (isValidGuid(url)) {
      //Just the report Id
      return url;
    } else {
      return undefined;
    }
  };

  const loadData = async () => {
    try {
      setIsLoading(true);

      // Defer the heavy runtime until needed
      const { models } = await import('powerbi-client');

      const token = await appContext.getAccessToken(powerBIClientRequest.scopes);
      const reportId = getIdFromUrl(props.url);

      let newTheme: import('powerbi-client').models.IReportTheme | undefined = undefined;
      if (appContext.useDarkMode) {
        newTheme = powerBI_dark;
      }

      if (token && reportId) {
        const embedConfig: pbi.IReportEmbedConfiguration = {
          type: 'report',
          id: reportId,
          embedUrl: 'https://app.powerbi.com/reportEmbed',
          accessToken: token,
          tokenType: models.TokenType.Aad,
          theme: { themeJson: newTheme },
          settings: {
            localeSettings: { language: appContext.user.language.codeWithCulture },
            layoutType: models.LayoutType.Custom,
            customLayout: {
              displayOption: models.DisplayOption.FitToPage,
            },
            bars: {
              actionBar: {
                visible: true,
              },
              statusBar: {
                visible: true,
              },
            },
            panes: {
              filters: {
                expanded: false,
                visible: true,
              },
              pageNavigation: {
                position: models.PageNavigationPosition.Bottom,
              },
            },
            persistentFiltersEnabled: true,
            personalBookmarksEnabled: true,
          },
        };

        setConfig(embedConfig);
      }
    } catch (err) {
      appContext.setError(err);
    } finally {
      setIsLoading(false);
    }
  };

  //
  // Main render
  //
  if (!hasMicrosoftIdentity) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPText>{t('identityproviders:Dashboard.PowerBIRequiresMicrosoft')}</IPText>
      </IPStack>
    );
  }

  if (isLoading) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPSpinner size={IPSpinnerSize.large} />
      </IPStack>
    );
  }

  if (!config) {
    return (
      <IPStack verticalFill horizontalAlign="center" verticalAlign="center">
        <IPText>{t('dashboard:InvalidReportId')}</IPText>
      </IPStack>
    );
  }

  return (
    <IPStack verticalFill>
      <IPStack.Item>
        <IPSeparator />
      </IPStack.Item>
      <IPStack.Item grow>
        <PowerBIEmbed embedConfig={config} cssClassName={'reportClass'} />
      </IPStack.Item>
    </IPStack>
  );
};

export default PowerBIReport;
